import { NavigationClient, type NavigationOptions } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useEffect } from "react";
import { type NavigateFunction, useNavigate } from "react-router";

/**
 * This class ensures MSAL uses React Router for navigation
 * instead of full page reloads.
 */
class CustomNavigationClient extends NavigationClient {
  private navigate: NavigateFunction;

  constructor(navigate: NavigateFunction) {
    super();
    this.navigate = navigate;
  }

  /**
   * Navigates to other pages within the same application
   */
  async navigateInternal(url: string, options: NavigationOptions): Promise<boolean> {
    const relativePath = url.replace(window.location.origin, "");
    if (options.noHistory) {
      this.navigate(relativePath, { replace: true });
    } else {
      this.navigate(relativePath);
    }
    return false; // Prevent MSAL from internal navigation
  }
}

/**
 * Integrated component to handle MSAL-Router connection and User State synchronization.
 * It must be rendered inside <BrowserRouter> to have access to 'navigate'.
 */
export const MsalIntegration = () => {
  const navigate = useNavigate();
  const { instance } = useMsal();

  // Navigation configuration
  useEffect(() => {
    const navigationClient = new CustomNavigationClient(navigate);
    instance.setNavigationClient(navigationClient);
  }, [navigate, instance]);

  return null;
};
